/**
 * `EndedScreen` — shown after a session is closed or the visitor left
 * (ARCHITECTURE §7). Offers a post-chat CSAT rating (when the embed id +
 * session id are known) and a button to start a new conversation.
 */

import { useMemo } from 'react';
import { parseEmbed } from '../config/parseEmbed';
import { visitorStore } from '../session/visitorStore';
import { CsatRating } from './CsatRating';

export interface EndedScreenProps {
  onRestart: () => void;
  /** Collect a separate agent/service rating alongside the overall one (org opt-in). */
  dualRating?: boolean;
  /** Org-configured "conversation ended" message (falls back to a default). */
  endText?: string;
  /** Whether to offer the "start a new chat" control (org opt-in; default on). */
  allowRestart?: boolean;
}

export function EndedScreen({
  onRestart,
  dualRating = false,
  endText,
  allowRestart = true,
}: EndedScreenProps) {
  // Resolve the embed id + just-ended session id needed to post a rating. Both
  // must be present; otherwise we render the plain ended state (never crash).
  const csat = useMemo<{ cid: string; sessionId: string } | null>(() => {
    let cid: string | null = null;
    try {
      cid = parseEmbed().cid;
    } catch {
      cid = null;
    }
    const sessionId = visitorStore.getSnapshot().session?.id ?? null;
    return cid && sessionId ? { cid, sessionId } : null;
  }, []);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-sm text-lc-text/80">
        {endText?.trim() || 'This conversation has ended.'}
      </p>

      {csat ? <CsatRating cid={csat.cid} sessionId={csat.sessionId} dualRating={dualRating} /> : null}

      {allowRestart ? (
        <button
          type="button"
          onClick={onRestart}
          style={{ fontFamily: 'var(--lc-btn-font)', fontSize: 'var(--lc-btn-font-size)' }}
          className="rounded-lc bg-lc-primary px-4 py-2 font-medium text-lc-on-primary"
        >
          Start a new chat
        </button>
      ) : null}
    </div>
  );
}
